import ReactDOMClient from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import singleSpaReact from 'single-spa-react';
import { type DefaultTheme, ThemeProvider } from 'styled-components';

import React from 'react';

import { TamentaiProvider } from '@poliedro/tamentai';

import {
  keepPreviousData,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';

import App from './App';
import { store } from './store';
import GlobalStyles from './styles/GlobalStyles';
import GlobalTheme from './styles/GlobalTheme';
import { loadTrackingScripts } from './utils/loadTrackingScripts';

const theme: DefaultTheme = GlobalTheme;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 0,
      gcTime: 0,
      placeholderData: keepPreviousData,
    },
  },
});

function AppRoot({ basename = '/' }: { basename?: string }) {
  return (
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <GlobalStyles />
        <QueryClientProvider client={queryClient}>
          <TamentaiProvider>
            <Provider store={store}>
              <BrowserRouter basename={basename}>
                <App />
              </BrowserRouter>
            </Provider>
          </TamentaiProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </React.StrictMode>
  );
}

// ── single-spa lifecycles ──────────────────────────────────────────────────

// single-spa-react@6 espera { render, hydrate } mas react-dom@19 os removeu.
// Passamos o createRoot como adapter para compatibilidade.
const ReactDOMAdapter = {
  render: (el: React.ReactElement, container: Element) => {
    ReactDOMClient.createRoot(container).render(el);
  },
};

const lifecycles = singleSpaReact({
  React,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ReactDOM: ReactDOMAdapter as any,
  rootComponent: AppRoot,
  errorBoundary() {
    return <div>Cosmos app failed to load.</div>;
  },
});

export const { bootstrap, mount, unmount } = lifecycles;

// ── Standalone bootstrap (dev server / standalone build) ───────────────────

if (!window.__POWERED_BY_SINGLE_SPA__) {
  loadTrackingScripts();

  const container = document.getElementById('root');
  if (!container) throw new Error('#root element not found');

  const root = ReactDOMClient.createRoot(container);
  root.render(<AppRoot />);
}
